/**
 * Admin API client for the feedback-analytics panel.
 *
 * @module feedbackAnalyticsApi
 */

import { apiGet } from '@/lib/apiUtils';

export interface ModuleRatingVolume {
  readonly module: string;
  readonly likeCount: number;
  readonly dislikeCount: number;
}

export interface ReasonVolume {
  readonly targetType: string;
  readonly reasonCode: string;
  readonly count: number;
}

export interface WrongLevelCrossCheck {
  readonly total: number;
  readonly answeredCorrectly: number;
  readonly answeredIncorrectly: number;
  readonly correctnessRate?: number | null;
}

export interface ReviewFunnel {
  readonly gradeDoubtDislikes: number;
  readonly requestsSubmitted: number;
  readonly requestsCompleted: number;
}

export interface ModuleCount {
  readonly module: string;
  readonly count: number;
}

export interface ReviewOperations {
  readonly totalRequests: number;
  readonly byModule: ModuleCount[];
  readonly avgTurnaroundHours?: number | null;
  readonly pendingBacklog: number;
  readonly completedCount: number;
  readonly cancelledCount: number;
  readonly completionRate?: number | null;
  readonly cancelRate?: number | null;
}

export interface ReviewerWorkloadItem {
  readonly reviewerId?: string | null;
  readonly reviewerName?: string | null;
  readonly handledCount: number;
  readonly inProgressCount: number;
  readonly avgTurnaroundHours?: number | null;
}

export interface FeedbackSummary {
  readonly totalRatings: number;
  readonly likeCount: number;
  readonly dislikeCount: number;
  readonly dislikeRate?: number | null;
  readonly affectedQuestions: number;
  readonly humanReviewRequests: number;
  readonly pendingBacklog: number;
  readonly completedReviews: number;
  readonly completionRate?: number | null;
}

export interface QuestionHotspotItem {
  readonly questionId: string;
  readonly questionText: string;
  readonly module?: string | null;
  readonly level?: string | null;
  readonly section?: number | null;
  readonly examType: string[];
  readonly likeCount: number;
  readonly dislikeCount: number;
  readonly totalRatings: number;
  readonly dislikeRate?: number | null;
  readonly recentRatingAt?: string | null;
}

export interface QuestionHotspots {
  readonly mostDisliked: QuestionHotspotItem[];
  readonly mostLiked: QuestionHotspotItem[];
  readonly highestDislikeRate: QuestionHotspotItem[];
}

export interface FeedbackEventTrace {
  readonly ratingId: string;
  readonly rating: 'like' | 'dislike' | string;
  readonly targetType: string;
  readonly targetId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly questionId?: string | null;
  readonly questionText: string;
  readonly module?: string | null;
  readonly level?: string | null;
  readonly section?: number | null;
  readonly examType: string[];
  readonly userId?: string | null;
  readonly username?: string | null;
  readonly email?: string | null;
  readonly sessionId?: string | null;
  readonly reasonCodes: string[];
  readonly freeText?: string | null;
  readonly isCorrect?: boolean | null;
  readonly points?: number | null;
  readonly answerText?: string | null;
  readonly expectedAnswer?: string | null;
  readonly explanation?: string | null;
  readonly aiFeedbackSummary?: string | null;
}

export interface PaginatedFeedbackEvents {
  readonly items: FeedbackEventTrace[];
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly totalPages: number;
}

export interface FeedbackAnalytics {
  readonly summary: FeedbackSummary;
  readonly likeDislikeByModule: ModuleRatingVolume[];
  readonly dislikeByReason: ReasonVolume[];
  readonly wrongLevel: WrongLevelCrossCheck;
  readonly funnel: ReviewFunnel;
  readonly reviewOperations: ReviewOperations;
  readonly reviewerWorkload: ReviewerWorkloadItem[];
  readonly questionHotspots: QuestionHotspots;
  readonly feedbackEvents: PaginatedFeedbackEvents;
}

export interface FeedbackAnalyticsFilters {
  readonly dateFrom?: string;
  readonly dateTo?: string;
  readonly module?: string;
  readonly targetType?: string;
  readonly reasonCode?: string;
  readonly rating?: string;
  readonly questionId?: string;
  readonly page?: number;
  readonly limit?: number;
}

function buildQuery(filters: FeedbackAnalyticsFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && value !== 'all') {
      const textValue = String(value);
      if (key === 'dateFrom' && /^\d{4}-\d{2}-\d{2}$/.test(textValue)) {
        params.append(key, `${value}T00:00:00`);
        return;
      }
      if (key === 'dateTo' && /^\d{4}-\d{2}-\d{2}$/.test(textValue)) {
        params.append(key, `${value}T23:59:59`);
        return;
      }
      params.append(key, textValue);
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function getFeedbackAnalytics(
  filters: FeedbackAnalyticsFilters = {},
): Promise<FeedbackAnalytics | null> {
  return apiGet<FeedbackAnalytics>(`/admin/feedback-analytics${buildQuery(filters)}`, {
    errorMessage: 'Failed to load feedback analytics',
    context: 'getFeedbackAnalytics',
  });
}
